import { useMemo } from "react";
import type { RJSFSchema } from "@rjsf/utils";

/**
 * Polymorphic result renderer.
 *
 * Classifies the output by examining the outcome's declared output_schema
 * (if provided) and falling back to shape heuristics. Supports:
 *
 *   • image-gallery  — array of HTTPS URLs (`.jpg|.png|.gif|.webp`)
 *   • video          — single URL ending in `.mp4|.webm|.mov`
 *   • audio          — single URL ending in `.mp3|.wav|.ogg`
 *   • url            — single generic URL (e.g. blog post permalink)
 *   • text           — string
 *   • json           — arbitrary object / array
 *   • unknown        — raw <pre> fallback
 */

type View =
  | { kind: "image-gallery"; urls: string[] }
  | { kind: "video"; src: string }
  | { kind: "audio"; src: string }
  | { kind: "url"; href: string; label?: string }
  | { kind: "text"; body: string }
  | { kind: "json"; value: unknown }
  | { kind: "unknown"; raw: unknown };

const IMAGE_RE = /\.(jpe?g|png|gif|webp|avif)(\?|$)/i;
const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;
const AUDIO_RE = /\.(mp3|wav|ogg|m4a)(\?|$)/i;

export interface ResultRendererProps {
  output: unknown;
  outputSchema?: RJSFSchema;
}

export function ResultRenderer({ output, outputSchema }: ResultRendererProps) {
  const view = useMemo(() => classify(output, outputSchema), [output, outputSchema]);
  return render(view);
}

function classify(output: unknown, schema?: RJSFSchema): View {
  if (output === null || output === undefined) {
    return { kind: "unknown", raw: output };
  }

  // If the schema has a top-level `type: array` of URLs, render as gallery.
  if (schema && typeof output === "object" && !Array.isArray(output)) {
    for (const [key, prop] of Object.entries(schema.properties ?? {})) {
      const value = (output as Record<string, unknown>)[key];
      const propSchema = prop as RJSFSchema;
      if (
        propSchema.type === "array" &&
        Array.isArray(value) &&
        value.every((v) => typeof v === "string" && /^https?:\/\//.test(v))
      ) {
        const urls = value as string[];
        if (urls.every((u) => IMAGE_RE.test(u))) {
          return { kind: "image-gallery", urls };
        }
      }
    }
  }

  // Single-URL outputs — { url: "..." } or { video_url: "..." } etc.
  if (typeof output === "object" && !Array.isArray(output)) {
    const obj = output as Record<string, unknown>;
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value !== "string") continue;
      if (!/^https?:\/\//.test(value)) continue;
      if (VIDEO_RE.test(value)) return { kind: "video", src: value };
      if (AUDIO_RE.test(value)) return { kind: "audio", src: value };
      if (IMAGE_RE.test(value)) return { kind: "image-gallery", urls: [value] };
      if (key.toLowerCase().includes("url") || key.toLowerCase().includes("link")) {
        return { kind: "url", href: value, label: obj.title as string | undefined };
      }
    }
  }

  if (typeof output === "string") {
    if (/^https?:\/\//.test(output)) return { kind: "url", href: output };
    return { kind: "text", body: output };
  }

  return { kind: "json", value: output };
}

function render(view: View) {
  switch (view.kind) {
    case "image-gallery":
      return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {view.urls.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block overflow-hidden rounded-lg border border-neutral-200 bg-white transition hover:border-emerald-300 hover:shadow-md dark:border-neutral-800 dark:bg-neutral-950"
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                className="aspect-video w-full object-cover transition group-hover:scale-[1.02]"
              />
            </a>
          ))}
        </div>
      );
    case "video":
      return (
        <video
          src={view.src}
          controls
          className="w-full rounded-lg border border-neutral-200 dark:border-neutral-800"
        />
      );
    case "audio":
      return <audio src={view.src} controls className="w-full" />;
    case "url":
      return (
        <a
          href={view.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded-lg bg-neutral-100 px-4 py-3 text-sm font-medium hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
          {view.label ?? view.href} ↗
        </a>
      );
    case "text":
      return (
        <pre className="whitespace-pre-wrap rounded-lg bg-neutral-50 p-4 text-sm text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
          {view.body}
        </pre>
      );
    case "json":
      return (
        <pre className="overflow-x-auto rounded-lg bg-neutral-50 p-4 text-xs text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
          {JSON.stringify(view.value, null, 2)}
        </pre>
      );
    default:
      return (
        <pre className="overflow-x-auto rounded-lg bg-neutral-50 p-4 text-xs text-neutral-500 dark:bg-neutral-900">
          {JSON.stringify(view.raw, null, 2)}
        </pre>
      );
  }
}
